import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Breadcrumbs,
  Grid,
  Divider,
  Button,
  Skeleton,
  Chip,
  useTheme,
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getServicesIaPlanByIdStore } from 'store/services-ia-plans/thunks';
import { servicesIaPlansMapper } from './mappers/services-ia-plans.mapper';
import {
  EditOutlined as EditIcon,
  CreditCardOutlined as CreditCardIcon,
  FormatListBulleted as ListIcon,
  StarsOutlined as StarIcon,
  ChatOutlined as ChatIcon,
  ImageOutlined as ImageIcon,
  MicOutlined as AudioIcon,
  DescriptionOutlined as DocIcon,
  TimelineOutlined as EmbeddingsIcon,
  MemoryOutlined as MemoryIcon,
  BuildOutlined as ToolsIcon,
  ManageSearchOutlined as RagIcon,
  CheckCircle as CheckCircleIcon,
  CancelOutlined as CancelIcon,
} from '@mui/icons-material';

export const DetailPlanes = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();

  const [item, setItem] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      const response = await dispatch(getServicesIaPlanByIdStore(id));
      if (response?.ok && response?.data) {
        setItem(response.data);
      } else {
        navigate('/manager/servicios-ia/comercial/planes');
      }
    };

    loadData();
  }, [id, dispatch, navigate]);

  if (!item)
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );

  const mappedDomain = servicesIaPlansMapper.toDomain(item);

  const capabilities = [
    { label: 'Chat', value: mappedDomain.allowChat, icon: <ChatIcon fontSize="small" /> },
    { label: 'Imágenes', value: mappedDomain.allowImages, icon: <ImageIcon fontSize="small" /> },
    { label: 'Audio', value: mappedDomain.allowAudio, icon: <AudioIcon fontSize="small" /> },
    { label: 'Documentos', value: mappedDomain.allowDocuments, icon: <DocIcon fontSize="small" /> },
    {
      label: 'Embeddings',
      value: mappedDomain.allowEmbeddings,
      icon: <EmbeddingsIcon fontSize="small" />,
    },
    { label: 'Memoria', value: mappedDomain.allowMemory, icon: <MemoryIcon fontSize="small" /> },
    { label: 'Herramientas', value: mappedDomain.allowTools, icon: <ToolsIcon fontSize="small" /> },
    { label: 'RAG', value: mappedDomain.allowRag, icon: <RagIcon fontSize="small" /> },
  ];

  const formatLimit = (value: any) => {
    if (value === null || value === undefined) return 'No definido';
    return value === 0 ? 'Ilimitado' : value;
  };

  return (
    <Box sx={{ width: '100%', mb: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
            Detalle del Plan
          </Typography>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/manager/plataforma-ia/comercial/planes">
              Planes IA
            </Link>
            <Typography color="text.primary">{mappedDomain.name}</Typography>
          </Breadcrumbs>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          component={Link}
          to={`/manager/servicios-ia/comercial/planes/${mappedDomain.id}/editar`}
          sx={{ borderRadius: 2 }}
        >
          Editar Plan
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ sm: 12, md: 4 }}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: theme.palette.primary.main,
                }}
              >
                <CreditCardIcon sx={{ fontSize: 40 }} />
              </Box>

              <Typography variant="h5" fontWeight={700} mb={1}>
                {mappedDomain.name}
              </Typography>

              <Chip
                label={mappedDomain.status === 1 ? 'Activo' : 'Inactivo'}
                color={mappedDomain.status === 1 ? 'success' : 'error'}
                size="small"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {mappedDomain.description || 'Sin descripción'}
              </Typography>

              <Divider sx={{ width: '100%', my: 3 }} />

              <Typography variant="h3" fontWeight={800} color="primary.main">
                ${Number(mappedDomain.monthlyPrice).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Mensual ({mappedDomain.currency})
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ sm: 12, md: 8 }}>
          <Card
            sx={{
              p: 4,
              borderRadius: 3,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              mb={3}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <ListIcon sx={{ color: theme.palette.primary.main }} />
              Límites y Condiciones
            </Typography>

            <Grid container spacing={3} mb={4}>
              <Grid size={{ sm: 12, md: 6 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Créditos Mensuales
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {mappedDomain.monthlyCredits}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ sm: 12, md: 6 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Peticiones / Día
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatLimit(mappedDomain.maxRequestsDay)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ sm: 12, md: 4 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Asistentes Max.
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatLimit(mappedDomain.maxAssistants)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ sm: 12, md: 4 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Agentes Max.
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatLimit(mappedDomain.maxAgents)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ sm: 12, md: 4 }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Usuarios Max.
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatLimit(mappedDomain.maxUsers)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography
              variant="h6"
              fontWeight={700}
              mb={3}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <StarIcon sx={{ color: theme.palette.primary.main }} />
              Capacidades Incluidas
            </Typography>

            <Grid container spacing={2}>
              {capabilities.map((cap, index) => (
                <Grid size={{ sm: 12, md: 4 }} key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: cap.value ? theme.palette.success.light : theme.palette.divider,
                      bgcolor: cap.value ? `${theme.palette.success.main}10` : 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        color: cap.value ? theme.palette.success.main : theme.palette.text.disabled,
                        display: 'flex',
                      }}
                    >
                      {cap.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={cap.value ? 600 : 400}
                      color={cap.value ? 'text.primary' : 'text.disabled'}
                    >
                      {cap.label}
                    </Typography>
                    <Box sx={{ ml: 'auto' }}>
                      {cap.value ? (
                        <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                      ) : (
                        <CancelIcon sx={{ color: theme.palette.text.disabled }} />
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
